
/**
 * The teacher's referral code and balance.
 *
 * Every field behind this is server-written and denied to the client by the
 * users-doc allowlist, so there is nothing to read locally — the code has to
 * be fetched, and claiming one has to be asked for.
 */
export interface ReferralState {
  code: string;
  credits: number;
  referrals: number;
  perSide: number;
}

async function authHeader(): Promise<Record<string, string>> {
  const { getAuth } = await import('firebase/auth');
  const currentUser = getAuth().currentUser;
  if (!currentUser) throw new Error('You must be signed in.');
  const idToken = await currentUser.getIdToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` };
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const res = await fetch('/.netlify/functions/referral', {
    method: 'POST',
    headers: await authHeader(),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Referral request failed');
  return data as T;
}

/** Fetch (creating on first ask) this teacher's code and current balance. */
export const getReferralState = () => call<ReferralState>({ action: 'me' });

/** Apply someone else's code. Server refuses self-referral, a second code,
 *  and any code applied after the first subscription. */
export const claimReferralCode = (code: string) =>
  call<{ ok: true; credits: number }>({ action: 'claim', code });

/**
 * Invite colleagues by email. The server sends it, so the referral does not
 * depend on the teacher remembering to forward a link — which is where most
 * referral programmes quietly die.
 */
export const sendReferralInvites = (emails: string[]) =>
  call<{ sent: number; failed: string[]; code: string }>({ action: 'invite', emails });
